import { useState, useEffect } from "react";
import { Plus, Tag, X, Check, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { TagIcon } from "@/components/tags/TagIcon";
import { useTagAssignment } from "@/hooks/useTagAssignment";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import * as tagsApi from "@/services/tagsApi";
import { mapApiTagMinimal } from "@/utils/tagMapping";

interface DeviceTagAssignmentProps {
  deviceId: string;
}

export const DeviceTagAssignment = ({ deviceId }: DeviceTagAssignmentProps) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const queryClient = useQueryClient();
  const { assignTag, removeTag, isLoading: isAssigning } = useTagAssignment("device");

  // Fetch tags assigned to this device
  const { data: assignedTags = [], isLoading: loadingAssigned } = useQuery({
    queryKey: ["device-tags", deviceId],
    queryFn: async () => {
      const tags = await tagsApi.getTagsByDevice(deviceId);
      return tags.map(mapApiTagMinimal);
    },
    enabled: !!deviceId,
  });

  // Fetch all available tags
  const { data: allTags = [] } = useQuery({
    queryKey: ["tags"],
    queryFn: async () => {
      const tags = await tagsApi.listTags();
      return tags.map(mapApiTagMinimal);
    },
    staleTime: 60_000,
  });

  const assignedIds = new Set(assignedTags.map((t) => t.id));

  const availableTags = allTags.filter(
    (t) => !assignedIds.has(t.id) && (t.name || "").toLowerCase().includes(search.toLowerCase())
  );

  const handleAssign = async (tagId: string) => {
    const success = await assignTag(deviceId, tagId);
    if (success) {
      queryClient.invalidateQueries({ queryKey: ["device-tags", deviceId] });
    }
  };

  const handleRemove = async (tagId: string) => {
    const success = await removeTag(deviceId, tagId);
    if (success) {
      queryClient.invalidateQueries({ queryKey: ["device-tags", deviceId] });
    }
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <Tag className="h-4 w-4 text-muted-foreground flex-shrink-0" />

      {loadingAssigned ? (
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      ) : assignedTags.length === 0 ? (
        <span className="text-sm text-muted-foreground">No tags assigned</span>
      ) : (
        assignedTags.map((tag) => (
          <Badge
            key={tag.id}
            variant="secondary"
            className="gap-1 group relative pr-6"
            style={{
              backgroundColor: `${tag.color}20`,
              borderColor: tag.color,
              color: tag.color,
            }}
          >
            <TagIcon iconName={tag.icon} color={tag.color} size={8} />
            {tag.name}
            <button
              onClick={() => handleRemove(tag.id)}
              disabled={isAssigning}
              className="absolute right-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/10 rounded-full p-0.5"
              aria-label={`Remove ${tag.name}`}
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))
      )}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="h-6 px-2 text-xs gap-1">
            <Plus className="h-3 w-3" />
            Add Tag
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-2" align="start">
          <Input
            placeholder="Search tags..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 text-sm mb-2"
            autoFocus
          />
          <div className="max-h-48 overflow-y-auto space-y-0.5">
            {availableTags.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-3">
                {search ? "No matching tags" : "All tags assigned"}
              </p>
            ) : (
              availableTags.map((tag) => (
                <button
                  key={tag.id}
                  onClick={() => handleAssign(tag.id)}
                  disabled={isAssigning}
                  className="flex items-center gap-2 w-full px-2 py-1.5 rounded-md text-sm hover:bg-muted transition-colors text-left disabled:opacity-50"
                >
                  <TagIcon iconName={tag.icon} color={tag.color} size={10} />
                  <span className="flex-1 truncate">{tag.name}</span>
                  <Badge variant="outline" className="text-[10px] h-4 capitalize">{tag.type}</Badge>
                </button>
              ))
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};
